/**
 * Chat FAB (Floating Action Button) Component
 *
 * Floating button to open the AI Chat panel.
 */

import React from 'react';
import { Fab, Badge, Tooltip, Zoom, Box, Typography } from '@mui/material';
import AiChatIcon from 'features/ui/app-layout/rail/ai-chat-icon';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import useAIChatStore from '../../../stores/ai-chat-store';

interface ChatFabProps {
  unreadCount?: number;
  pulse?: boolean;
}

const ChatFab: React.FC<ChatFabProps> = ({ unreadCount = 0, pulse = false }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const { isOpen, isMinimized, toggle, maximize } = useAIChatStore((state) => ({
    isOpen: state.isOpen,
    isMinimized: state.isMinimized,
    toggle: state.toggle,
    maximize: state.maximize,
  }));

  const handleClick = () => {
    if (isMinimized) {
      maximize();
    } else {
      toggle();
    }
  };

  // Show minimized state
  if (isMinimized) {
    return (
      <Zoom in>
        <Box className={classes.minimizedContainer} onClick={handleClick}>
          <Box className={classes.minimizedHeader}>
            <AiChatIcon className={classes.minimizedIcon} />
            <Typography variant="caption" className={classes.minimizedText}>
              {t('paiAssistant', 'SAIA')}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Badge badgeContent={unreadCount} color="error" className={classes.minimizedBadge} />
          )}
        </Box>
      </Zoom>
    );
  }

  return (
    <Tooltip
      title={isOpen ? t('closeChat', 'Close chat') : t('openChat', 'Chat with SAIA')}
      placement="left"
    >
      <Fab
        color="primary"
        className={cx(classes.fab, pulse && !isOpen && 'pulse')}
        onClick={handleClick}
        aria-label={isOpen ? t('closeChat', 'Close chat') : t('openChat', 'Open chat')}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          invisible={unreadCount === 0 || isOpen}
          className={classes.badge}
        >
          <Box className={classes.iconContainer}>
            <AiChatIcon className={cx(classes.icon, isOpen && 'hidden')} />
            <CloseIcon className={cx(classes.icon, classes.closeIcon, !isOpen && 'hidden')} />
          </Box>
        </Badge>
      </Fab>
    </Tooltip>
  );
};

const useStyles = makeStyles()((theme) => ({
  fab: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1200,
    width: 56,
    height: 56,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    boxShadow: `0 4px 20px ${theme.palette.primary.main}66`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'scale(1.05)',
      boxShadow: `0 6px 24px ${theme.palette.primary.main}80`,
    },
    '&.pulse': {
      animation: 'fabPulse 2s infinite',
    },
    '@keyframes fabPulse': {
      '0%': {
        boxShadow: `0 4px 20px ${theme.palette.primary.main}66`,
      },
      '50%': {
        boxShadow: `0 4px 30px ${theme.palette.primary.main}99`,
      },
      '100%': {
        boxShadow: `0 4px 20px ${theme.palette.primary.main}66`,
      },
    },
    [theme.breakpoints.down('sm')]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
  },
  badge: {
    '& .MuiBadge-badge': {
      top: -4,
      right: -4,
    },
  },
  iconContainer: {
    position: 'relative',
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    position: 'absolute',
    transition: 'opacity 0.2s, transform 0.2s',
    '&.hidden': {
      opacity: 0,
      transform: 'rotate(90deg) scale(0)',
    },
  },
  closeIcon: {
    fontSize: '1.5rem',
  },
  minimizedContainer: {
    position: 'fixed',
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: 24,
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    color: theme.palette.primary.contrastText,
    cursor: 'pointer',
    boxShadow: `0 4px 20px ${theme.palette.primary.main}66`,
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'scale(1.02)',
      boxShadow: `0 6px 24px ${theme.palette.primary.main}80`,
    },
    [theme.breakpoints.down('sm')]: {
      bottom: theme.spacing(2),
      right: theme.spacing(2),
    },
  },
  minimizedHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  minimizedIcon: {
    fontSize: '1.25rem',
  },
  minimizedText: {
    fontWeight: 500,
    color: theme.palette.primary.contrastText,
  },
  minimizedBadge: {
    '& .MuiBadge-badge': {
      position: 'static',
    },
  },
}));

export default ChatFab;

